import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

interface Product {
  id: string | number;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  category: string;
  usdtPrice?: number;
}

interface PaymentSelectModalProps {
  product: Product;
  quantity?: number;
  onClose: () => void;
}

export default function PaymentSelectModal({ product, quantity = 1, onClose }: PaymentSelectModalProps) {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const formatKRW = (n: number) => n.toLocaleString('ko-KR');

  const handleKRWPay = () => {
    addToCart({
      id: String(product.id),
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      category: product.category,
    }, quantity);
    onClose();
    navigate('/order');
  };

  const handleUSDTPay = () => {
    onClose();
    navigate('/point-purchase');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 모달 */}
      <div className="relative bg-white w-full sm:max-w-sm sm:mx-4 rounded-t-3xl sm:rounded-2xl overflow-hidden">
        {/* 모바일 핸들 */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
          aria-label="닫기"
        >
          <i className="ri-close-line text-gray-600 text-base w-4 h-4 flex items-center justify-center" />
        </button>

        <div className="px-5 pt-5 pb-6">
          {/* 제품 미리보기 */}
          <div className="flex items-center gap-3 mb-5 pr-8">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-stone-50 flex-shrink-0 border border-gray-100">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 mb-0.5">결제 방법 선택</p>
              <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">{product.name}</h3>
              {quantity > 1 && (
                <p className="text-xs text-gray-500 mt-0.5">수량 {quantity}개</p>
              )}
            </div>
          </div>

          {/* 가격 요약 */}
          <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">총 결제금액</span>
            </div>
            <div className="text-right">
              <p className="text-base font-black text-gray-900">
                {formatKRW(product.price * quantity)}원
              </p>
              {product.usdtPrice && (
                <p className="text-xs font-bold text-teal-600">
                  ≈ USDT ${product.usdtPrice * quantity}
                </p>
              )}
            </div>
          </div>

          {/* 결제 방법 선택 */}
          <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
            결제 방법을 선택하세요
          </p>

          <div className="space-y-3">
            {/* 원화(KRW) 결제 */}
            <button
              type="button"
              onClick={handleKRWPay}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100 transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-emerald-600 rounded-xl flex-shrink-0 group-hover:bg-emerald-700 transition-colors">
                <i className="ri-won-line text-white text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-gray-900">원화(KRW) 결제</p>
                <p className="text-xs text-gray-500 mt-0.5">카드 · 계좌이체 · 카카오페이 · 네이버페이</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 text-emerald-600">
                <span className="text-sm font-black">{formatKRW(product.price * quantity)}원</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </div>
            </button>

            {/* USDT 결제 */}
            <button
              type="button"
              onClick={handleUSDTPay}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-teal-200 bg-teal-50 hover:border-teal-400 hover:bg-teal-100 transition-all cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-teal-500 rounded-xl flex-shrink-0 group-hover:bg-teal-600 transition-colors">
                <i className="ri-bit-coin-line text-white text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  USDT 결제
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-500 text-white text-[10px] font-black">
                    ERC-20
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">암호화폐 · 지갑 주소로 직접 전송</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 text-teal-600">
                {product.usdtPrice ? (
                  <span className="text-sm font-black">${product.usdtPrice * quantity}</span>
                ) : (
                  <span className="text-sm font-black">USDT</span>
                )}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </div>
            </button>
          </div>

          {/* 하단 안내 */}
          <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
            USDT 결제는 ERC-20 네트워크 전용입니다<br />
            TX Hash 제출 후 관리자 확인을 거쳐 처리됩니다
          </p>
        </div>
      </div>
    </div>
  );
}